import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Volume2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export function IOSPWAAudioHint() {
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuth();
  const prevUserRef = useRef<typeof user>(undefined);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Check if iOS PWA
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    
    // Skip the first render to capture initial state
    if (!initializedRef.current) {
      prevUserRef.current = user;
      initializedRef.current = true;
      return;
    }
    
    // Detect login transition: previous user was null, now we have a user
    const wasLoggedOut = prevUserRef.current === null;
    const isNowLoggedIn = user !== null;
    const justLoggedIn = wasLoggedOut && isNowLoggedIn;
    
    // Show modal if: iOS PWA + user just logged in
    if (isIOS && isStandalone && justLoggedIn) {
      setShowModal(true);
    }
    
    // Update the ref for next comparison
    prevUserRef.current = user;
  }, [user]);

  const handleDismiss = () => {
    setShowModal(false);
  };

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-primary" />
            Audio Playback Tip
          </DialogTitle>
          <DialogDescription className="pt-4 space-y-3">
            <p>
              If audio doesn't play on the first try, here's a quick fix:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Tap <strong>Pause</strong> on the player</li>
              <li>Switch to another app briefly (don't close PodBrief)</li>
              <li>Return to PodBrief and tap <strong>Play</strong></li>
            </ol>
            <p className="text-sm text-muted-foreground">
              This is a one-time iOS quirk — audio will work smoothly after that!
            </p>
          </DialogDescription>
        </DialogHeader>
        <Button onClick={handleDismiss} className="w-full mt-4">
          Got it!
        </Button>
      </DialogContent>
    </Dialog>
  );
}
